/** Recover desktop composer pastes whose native editing action was dropped. */
export function installDesktopPasteRecovery(): () => void {
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return () => {}
  const pending = new Set<number>()
  function onPaste(event: ClipboardEvent) {
    const textarea = event.target
    if (event.defaultPrevented || !(textarea instanceof HTMLTextAreaElement)
      || !textarea.classList.contains('agentchat-composer-textarea')
      || textarea.disabled || textarea.readOnly) return
    let text = event.clipboardData?.getData('text/plain').replace(/\r\n?/g, '\n')
    if (!text) return
    const before = textarea.value
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const href = window.location.href
    if (textarea.maxLength >= 0) {
      const available = Math.max(0, textarea.maxLength - before.length + end - start)
      text = text.slice(0, available)
      // Do not leave half a surrogate pair at a field's length limit.
      if (/[\uD800-\uDBFF]$/.test(text)) text = text.slice(0, -1)
    }
    const expected = before.slice(0, start) + text + before.slice(end)
    if (expected === before) return
    const pastedText = text
    const timer = window.setTimeout(() => {
      pending.delete(timer)
      if (event.defaultPrevented || !textarea.isConnected || textarea.disabled || textarea.readOnly
        || window.location.href !== href || document.activeElement !== textarea
        || textarea.value !== before || textarea.selectionStart !== start || textarea.selectionEnd !== end) return
      // The default paste action is synchronous. An unchanged field on the next
      // task is the observed Chromium failure, not a delayed successful paste.
      try {
        document.execCommand('insertText', false, pastedText)
      } catch {
        // Fall through to a verified value/input update if native editing fails.
      }
      if (textarea.value === expected) return
      const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
      if (setter) setter.call(textarea, expected)
      else textarea.value = expected
      const caret = start + pastedText.length
      textarea.setSelectionRange(caret, caret)
      // Bypass React's value tracker before notifying its normal input handler.
      textarea.dispatchEvent(new InputEvent('input', {
        bubbles: true, inputType: 'insertFromPaste', data: pastedText,
      }))
    }, 0)
    pending.add(timer)
  }
  document.addEventListener('paste', onPaste)
  return () => {
    document.removeEventListener('paste', onPaste)
    pending.forEach((timer) => window.clearTimeout(timer))
    pending.clear()
  }
}
